// Analytics-dashboard voor /online-coaching.
// Verborgen route (/online-coaching/dashboard, noindex) — leest geaggregeerde
// events uit het floawch-backend via /api/public/lp-dashboard (key-beveiligd).
import { useState, useEffect, useCallback, useRef } from 'react';
import { applyNoIndex } from '../../utils/lpTracking';
import './Dashboard.css';

// Lokaal ontwikkelen: draai floawch op :3000, dan praat het dashboard daartegen
const API_URL = window.location.hostname.includes('localhost')
  ? 'http://localhost:3000/api/public/lp-dashboard'
  : 'https://hl-project.nl/api/public/lp-dashboard';
const KEY_STORAGE = 'lp_dash_key';

const RANGES = [
  { days: 7, label: '7 dagen' },
  { days: 14, label: '14 dagen' },
  { days: 30, label: '30 dagen' },
  { days: 90, label: '90 dagen' }
];

// Vandaag als YYYY-MM-DD in Amsterdamse tijd (sv-SE geeft ISO-notatie)
const todayStr = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });

// Gevalideerd donker palet (dataviz-skill): 2 categorische series + ordinale funnel-ramp
const C = {
  series1: '#3987e5', // weergaven
  series2: '#199e70', // sessies
  funnel: ['#9ec5f4', '#5598e7', '#2a78d6', '#184f95'],
  grid: '#2c2a26',
  axis: '#3a3733',
  ink: '#f5f2ef',
  ink2: '#b5ada6',
  muted: '#8d857f'
};

const nf = new Intl.NumberFormat('nl-NL');
const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const fmtSecs = (s) => {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};
const fmtDay = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};

// ---- Lijngrafiek: weergaven + sessies per dag (2 series, crosshair-tooltip) ----
function DailyChart({ daily }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const W = 720, H = 260, P = { t: 16, r: 16, b: 34, l: 44 };
  const data = daily || [];

  if (data.length === 0) {
    return <p className="lpd-empty">Nog geen data in deze periode.</p>;
  }

  const maxVal = Math.max(1, ...data.map((d) => Math.max(d.views, d.sessions)));
  // Nette bovengrens voor de y-as
  const step = Math.max(1, Math.ceil(maxVal / 4));
  const niceStep = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000].find((s) => s >= step) || step;
  const yMax = niceStep * 4;

  const x = (i) => (data.length === 1 ? W / 2 : P.l + (i / (data.length - 1)) * (W - P.l - P.r));
  const y = (v) => H - P.b - (v / yMax) * (H - P.t - P.b);
  const line = (key) => data.map((d, i) => `${x(i)},${y(d[key])}`).join(' ');

  const onMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    let bestDist = Infinity;
    data.forEach((_, i) => {
      const dist = Math.abs(x(i) - px);
      if (dist < bestDist) { bestDist = dist; best = i; }
    });
    setHover(best);
  };

  const xTickEvery = Math.max(1, Math.ceil(data.length / 6));

  return (
    <div className="lpd-chart-wrap">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label="Paginaweergaven en unieke sessies per dag"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {[1, 2, 3, 4].map((i) => (
          <g key={i}>
            <line x1={P.l} x2={W - P.r} y1={y(niceStep * i)} y2={y(niceStep * i)} stroke={C.grid} strokeWidth="1" />
            <text x={P.l - 8} y={y(niceStep * i) + 4} textAnchor="end" fontSize="11" fill={C.muted}>
              {nf.format(niceStep * i)}
            </text>
          </g>
        ))}
        <line x1={P.l} x2={W - P.r} y1={H - P.b} y2={H - P.b} stroke={C.axis} strokeWidth="1" />
        {data.map((d, i) =>
          i % xTickEvery === 0 ? (
            <text key={i} x={x(i)} y={H - P.b + 18} textAnchor="middle" fontSize="11" fill={C.muted}>
              {fmtDay(d.date)}
            </text>
          ) : null
        )}

        {hover != null && (
          <line x1={x(hover)} x2={x(hover)} y1={P.t} y2={H - P.b} stroke={C.axis} strokeWidth="1" />
        )}

        <polyline points={line('views')} fill="none" stroke={C.series1} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <polyline points={line('sessions')} fill="none" stroke={C.series2} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* Bij weinig datapunten ook dots tekenen (één punt = onzichtbare lijn) */}
        {data.length <= 21 &&
          data.map((d, i) => (
            <g key={`dots-${i}`}>
              <circle cx={x(i)} cy={y(d.views)} r="3" fill={C.series1} stroke="#191511" strokeWidth="2" />
              <circle cx={x(i)} cy={y(d.sessions)} r="3" fill={C.series2} stroke="#191511" strokeWidth="2" />
            </g>
          ))}

        {hover != null && (
          <>
            <circle cx={x(hover)} cy={y(data[hover].views)} r="4.5" fill={C.series1} stroke="#191511" strokeWidth="2" />
            <circle cx={x(hover)} cy={y(data[hover].sessions)} r="4.5" fill={C.series2} stroke="#191511" strokeWidth="2" />
          </>
        )}
      </svg>

      {hover != null && (
        <div
          className="lpd-tooltip"
          style={{ left: `${(x(hover) / W) * 100}%`, transform: x(hover) > W * 0.7 ? 'translateX(-105%)' : 'translateX(12px)' }}
        >
          <div className="lpd-tooltip-date">{fmtDay(data[hover].date)}</div>
          <div><span className="lpd-swatch" style={{ background: C.series1 }} /> Weergaven: <strong>{nf.format(data[hover].views)}</strong></div>
          <div><span className="lpd-swatch" style={{ background: C.series2 }} /> Sessies: <strong>{nf.format(data[hover].sessions)}</strong></div>
        </div>
      )}

      <div className="lpd-legend">
        <span><span className="lpd-swatch" style={{ background: C.series1 }} /> Paginaweergaven</span>
        <span><span className="lpd-swatch" style={{ background: C.series2 }} /> Unieke sessies</span>
      </div>
    </div>
  );
}

// ---- Funnel: horizontale bars in ordinale blauw-ramp -------------------------
function Funnel({ funnel }) {
  const steps = [
    { key: 'page_view', label: 'Bezocht de pagina' },
    { key: 'email_typing_started', label: 'Begon e-mail te typen' },
    { key: 'email_submit', label: 'E-mail achtergelaten' },
    { key: 'intake_click', label: 'Doorgeklikt naar intake' }
  ];
  const base = funnel.page_view || 0;
  if (base === 0) return <p className="lpd-empty">Nog geen data in deze periode.</p>;

  return (
    <div className="lpd-funnel">
      {steps.map((s, i) => {
        const val = funnel[s.key] || 0;
        const prev = i === 0 ? val : funnel[steps[i - 1].key] || 0;
        return (
          <div key={s.key} className="lpd-funnel-row">
            <span className="lpd-funnel-label">{s.label}</span>
            <div className="lpd-funnel-track">
              <div
                className="lpd-funnel-bar"
                style={{ width: `${Math.max(pct(val, base), val > 0 ? 2 : 0)}%`, background: C.funnel[i] }}
              />
            </div>
            <span className="lpd-funnel-value">
              <strong>{nf.format(val)}</strong> · {pct(val, base)}%
              {i > 0 && <em className="lpd-funnel-step"> ({pct(val, prev)}% v.d. vorige stap)</em>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---- Kolommen: hoe diep scrollen bezoekers ----------------------------------
function ScrollChart({ scrollDist, totalSessions }) {
  const milestones = [25, 50, 75, 90, 100];
  const byPct = {};
  (scrollDist || []).forEach((r) => { byPct[r.pct] = r.sessions; });
  const reached = milestones.map((m) => byPct[m] || 0);
  const under25 = Math.max(0, totalSessions - reached.reduce((a, b) => a + b, 0));
  const buckets = [
    { label: '< 25%', value: under25 },
    ...milestones.map((m, i) => ({ label: `tot ${m}%`, value: reached[i] }))
  ];

  const W = 460, H = 200, P = { t: 26, r: 8, b: 26, l: 8 };
  const maxVal = Math.max(1, ...buckets.map((b) => b.value));
  const slot = (W - P.l - P.r) / buckets.length;
  const barW = Math.min(24, slot * 0.5);

  if (totalSessions === 0) return <p className="lpd-empty">Nog geen data in deze periode.</p>;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Diepste scrollpunt per sessie">
      <line x1={P.l} x2={W - P.r} y1={H - P.b} y2={H - P.b} stroke={C.axis} strokeWidth="1" />
      {buckets.map((b, i) => {
        const cx = P.l + slot * i + slot / 2;
        const h = (b.value / maxVal) * (H - P.t - P.b);
        return (
          <g key={b.label}>
            <rect
              x={cx - barW / 2}
              y={H - P.b - h}
              width={barW}
              height={Math.max(h, b.value > 0 ? 2 : 0)}
              rx="4"
              fill={C.series1}
            >
              <title>{`${b.label}: ${nf.format(b.value)} sessies (${pct(b.value, totalSessions)}%)`}</title>
            </rect>
            {/* Ronding alleen aan de datakant: onderkant vlak op de baseline */}
            {h > 4 && <rect x={cx - barW / 2} y={H - P.b - 4} width={barW} height={4} fill={C.series1} />}
            <text x={cx} y={H - P.b - h - 7} textAnchor="middle" fontSize="12" fill={C.ink2}>
              {nf.format(b.value)}
            </text>
            <text x={cx} y={H - P.b + 17} textAnchor="middle" fontSize="11" fill={C.muted}>
              {b.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---- Simpele datatabel --------------------------------------------------------
function DataTable({ columns, rows, emptyText = 'Nog geen data.' }) {
  if (!rows || rows.length === 0) return <p className="lpd-empty">{emptyText}</p>;
  return (
    <div className="lpd-table-wrap">
      <table className="lpd-table">
        <thead>
          <tr>{columns.map((c) => <th key={c.label} className={c.num ? 'lpd-num' : ''}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.label} className={c.num ? 'lpd-num' : ''}>{c.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div className="lpd-tile">
      <span className="lpd-tile-label">{label}</span>
      <span className="lpd-tile-value">{value}</span>
      {sub && <span className="lpd-tile-sub">{sub}</span>}
    </div>
  );
}

const CTA_LABELS = {
  topbar: 'Topbar — "Start gratis"',
  future_section: 'Future-sectie — "Start vandaag"',
  sticky: 'Sticky mobiele knop',
  footer: 'Footer — "Start 14 dagen gratis"'
};

const SECTION_LABELS = {
  herkenning: '1. Herken je dit?',
  future_pacing: '2. Stel je voor (week 1/4/12)',
  resultaten: '3. Resultaten & PR’s',
  systeem: '4. Het systeem',
  app: '5. De app',
  reviews: '6. Reviews',
  coach: '7. Jouw coach',
  aanbod: '8. Het aanbod',
  voor_wie: '9. Voor wie (niet)',
  faq: '10. FAQ',
  final_cta: '11. Laatste CTA'
};

function LpDashboard() {
  const [key, setKey] = useState(() => localStorage.getItem(KEY_STORAGE) || '');
  const [keyInput, setKeyInput] = useState('');
  const [days, setDays] = useState(30);
  // Expliciete periode ({from, to} als YYYY-MM-DD) heeft voorrang op de dagen-presets
  const [range, setRange] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => applyNoIndex(), []);

  const load = useCallback(async (activeKey, activeDays, activeRange) => {
    setLoading(true);
    setError(null);
    try {
      const period = activeRange?.from
        ? `from=${activeRange.from}&to=${activeRange.to || activeRange.from}`
        : `days=${activeDays}`;
      const res = await fetch(`${API_URL}?key=${encodeURIComponent(activeKey)}&${period}&page=online_coaching`);
      if (res.status === 401) {
        localStorage.removeItem(KEY_STORAGE);
        setKey('');
        setError('Ongeldige sleutel.');
        setData(null);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError('Kon de data niet laden. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key, days, range);
  }, [key, days, range, load]);

  const submitKey = (e) => {
    e.preventDefault();
    const trimmed = keyInput.trim();
    if (!trimmed) return;
    localStorage.setItem(KEY_STORAGE, trimmed);
    setKey(trimmed);
  };

  // --- Sleutel-scherm ---
  if (!key) {
    return (
      <div className="lpd-page lpd-gate">
        <form className="lpd-gate-card" onSubmit={submitKey}>
          <h1>LP Analytics</h1>
          <p>Voer de dashboard-sleutel in om de statistieken van /online-coaching te bekijken.</p>
          <input
            type="password"
            placeholder="Dashboard-sleutel"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            autoFocus
          />
          <button type="submit">Bekijk dashboard</button>
          {error && <p className="lpd-error">{error}</p>}
        </form>
      </div>
    );
  }

  const totals = data?.totals || { pageviews: 0, sessions: 0, clients: 0 };
  const funnel = data?.funnel || {};
  const leads = funnel.email_submit || 0;

  return (
    <div className="lpd-page">
      <div className="lpd-inner">
      <header className="lpd-header">
        <div>
          <h1>LP Analytics — /online-coaching</h1>
          <p className="lpd-subtitle">
            {data
              ? `${data.range.days
                  ? `Laatste ${data.range.days} dagen`
                  : data.range.fromDay === data.range.toDay
                    ? fmtDay(data.range.fromDay)
                    : `${fmtDay(data.range.fromDay)} – ${fmtDay(data.range.toDay)}`
                } · bijgewerkt ${new Date(data.generatedAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`
              : 'Laden…'}
          </p>
        </div>
        <div className="lpd-controls">
          {RANGES.map((r) => (
            <button
              key={r.days}
              className={`lpd-range ${!range && days === r.days ? 'lpd-range-active' : ''}`}
              onClick={() => { setRange(null); setDays(r.days); }}
            >
              {!range && days === r.days ? '✓ ' : ''}{r.label}
            </button>
          ))}
          <button
            className={`lpd-range ${range && range.from === todayStr() && range.to === todayStr() ? 'lpd-range-active' : ''}`}
            onClick={() => setRange({ from: todayStr(), to: todayStr() })}
          >
            {range && range.from === todayStr() && range.to === todayStr() ? '✓ ' : ''}Vandaag
          </button>
          <span className={`lpd-daterange ${range ? 'lpd-daterange-active' : ''}`}>
            <input
              type="date"
              value={range?.from || ''}
              max={todayStr()}
              onChange={(e) => {
                const from = e.target.value;
                if (!from) return;
                setRange((prev) => ({ from, to: prev?.to && prev.to >= from ? prev.to : from }));
              }}
            />
            <span className="lpd-daterange-sep">t/m</span>
            <input
              type="date"
              value={range?.to || ''}
              min={range?.from || undefined}
              max={todayStr()}
              onChange={(e) => {
                const to = e.target.value;
                if (!to) return;
                setRange((prev) => ({ from: prev?.from && prev.from <= to ? prev.from : to, to }));
              }}
            />
          </span>
          <button className="lpd-range" onClick={() => load(key, days, range)} disabled={loading}>
            {loading ? 'Laden…' : 'Vernieuw'}
          </button>
          <button
            className="lpd-range lpd-logout"
            onClick={() => { localStorage.removeItem(KEY_STORAGE); setKey(''); setData(null); }}
          >
            Sleutel wissen
          </button>
        </div>
      </header>

      {error && <p className="lpd-error">{error}</p>}

      {data && (
        <>
          {/* KPI-rij */}
          <div className="lpd-tiles">
            <StatTile label="Unieke bezoekers" value={nf.format(totals.clients)} sub={`${nf.format(totals.sessions)} sessies`} />
            <StatTile label="Paginaweergaven" value={nf.format(totals.pageviews)} />
            <StatTile label="E-mail leads" value={nf.format(leads)} sub={`${pct(leads, funnel.page_view || 0)}% van sessies`} />
            <StatTile label="Intake-kliks" value={nf.format(funnel.intake_click || 0)} sub={`${pct(funnel.intake_click || 0, leads)}% van leads`} />
            <StatTile label="Gem. tijd op pagina" value={fmtSecs(data.exit.avgSecs)} sub={`o.b.v. ${nf.format(data.exit.sessions)} sessies`} />
            <StatTile label="Gem. scroll bij vertrek" value={data.exit.avgScroll != null ? `${Math.round(data.exit.avgScroll)}%` : '—'} />
          </div>

          {/* Bezoeken per dag */}
          <section className="lpd-card">
            <h2>Bezoeken per dag</h2>
            <DailyChart daily={data.daily} />
          </section>

          <div className="lpd-grid">
            <section className="lpd-card">
              <h2>Funnel</h2>
              <p className="lpd-card-sub">Unieke sessies per stap</p>
              <Funnel funnel={funnel} />
            </section>

            <section className="lpd-card">
              <h2>Scroll-diepte</h2>
              <p className="lpd-card-sub">Diepste punt per sessie</p>
              <ScrollChart scrollDist={data.scrollDist} totalSessions={funnel.page_view || 0} />
            </section>

            <section className="lpd-card">
              <h2>CTA-klikken</h2>
              <DataTable
                columns={[
                  { label: 'Knop', render: (r) => CTA_LABELS[r.location] || r.location || 'onbekend' },
                  { label: 'Klikken', num: true, render: (r) => nf.format(r.clicks) },
                  { label: 'Sessies', num: true, render: (r) => nf.format(r.sessions) }
                ]}
                rows={data.ctas}
                emptyText="Nog geen CTA-klikken."
              />
            </section>

            <section className="lpd-card">
              <h2>E-mail per formulier</h2>
              <DataTable
                columns={[
                  { label: 'Formulier', render: (r) => ({ hero: 'Hero (boven)', offer: 'Aanbod-sectie', final: 'Laatste CTA' }[r.location] || r.location || 'onbekend') },
                  { label: 'Submits', num: true, render: (r) => nf.format(r.submits) },
                  { label: 'Sessies', num: true, render: (r) => nf.format(r.sessions) }
                ]}
                rows={data.emailByLocation}
                emptyText="Nog geen e-mail-submits."
              />
            </section>

            <section className="lpd-card">
              <h2>Secties gezien</h2>
              <p className="lpd-card-sub">% van sessies dat de sectie in beeld kreeg</p>
              <DataTable
                columns={[
                  { label: 'Sectie', render: (r) => SECTION_LABELS[r.section] || r.section },
                  { label: 'Sessies', num: true, render: (r) => nf.format(r.sessions) },
                  { label: '%', num: true, render: (r) => `${pct(r.sessions, funnel.page_view || 0)}%` }
                ]}
                rows={[...(data.sections || [])].sort((a, b) => {
                  const order = Object.keys(SECTION_LABELS);
                  const ia = order.indexOf(a.section);
                  const ib = order.indexOf(b.section);
                  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                })}
                emptyText="Nog geen sectie-weergaven."
              />
            </section>

            <section className="lpd-card">
              <h2>Verkeersbronnen</h2>
              <DataTable
                columns={[
                  { label: 'Bron', render: (r) => r.source },
                  { label: 'Sessies', num: true, render: (r) => nf.format(r.sessions) },
                  { label: 'Weergaven', num: true, render: (r) => nf.format(r.views) }
                ]}
                rows={data.sources}
              />
            </section>

            <section className="lpd-card">
              <h2>Apparaat</h2>
              <DataTable
                columns={[
                  { label: 'Type', render: (r) => r.device },
                  { label: 'Sessies', num: true, render: (r) => nf.format(r.sessions) },
                  { label: '%', num: true, render: (r) => `${pct(r.sessions, totals.sessions)}%` }
                ]}
                rows={data.devices}
              />
            </section>

            <section className="lpd-card">
              <h2>Meest geopende FAQ-vragen</h2>
              <DataTable
                columns={[
                  { label: 'Vraag', render: (r) => String(r.question) },
                  { label: 'Geopend', num: true, render: (r) => nf.format(r.opens) }
                ]}
                rows={data.faq}
                emptyText="Nog geen FAQ-opens."
              />
            </section>
          </div>
        </>
      )}
      </div>
    </div>
  );
}

export default LpDashboard;
